import * as React from "react";
import {
  Identifier,
  Layout,
  useCreatePath,
  useDataProvider,
  useRedirect,
  useLogout
} from "react-admin";
import { MainMenu } from "./Menu";
import { TopBar } from "./AppBar";
import { AppLocationContext, Breadcrumb } from "../enterprise/ra-navigation";
import AuthProvider from "../providers/AuthProvider";
import { getRoles } from '../common/AuthFunctions';

export const MainLayout = ({ children, ...rest }) => {
  const redirect = useRedirect();
  const logout = useLogout();

  if (!AuthProvider.isLoggedIn()) {
    redirect('/login');
  }

  if (getRoles().length === 0) {
    logout();
  }

  return (
    <AppLocationContext>
      <Layout 
        {...rest} 
        menu={MainMenu} 
        appBar={TopBar}
      >
        <CustomBreadcrumb />
        {children}
      </Layout>
    </AppLocationContext>
  );
};

const CustomBreadcrumb = () => {
  const resources = [
    "pages",
    "articles",
    "article/tags",
    "article/categories",
    "users",
    "navigations",
    "navigation/items",
    "promotions",
    "faqs",
    "products",
    "product/attribute-sets",
    "product/attributes",
    "product/categories",
    "importer/sheets",
    "importer",
    "taxes",
    "tax/items",
    "orders",
    "configurations",
    "product/stock",
    "product/stickers",
    "product/price-calculators"
  ];

  const dataProvider = useDataProvider();

  return (
    <Breadcrumb>
      {resources.map((resource: string, index: number) => {
        return (
          <CustomBreadcrumbItem
            key={index}
            resource={resource}
          ></CustomBreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );
};

function CustomBreadcrumbItem(props) {
  const { resource } = props;

  function createLabel(record, type: "Edit" | "Show") {
    let entity = record as {
      title: string;
      name: string;
      first_name: string;
      last_name: string;
      id: string;
    };
    if (entity) {
      if (entity.title) {
        return `${entity.title} (${entity.id})`;
      }
      if (entity.name) {
        return `${entity.name} (${entity.id})`;
      }
      if (entity.first_name && entity.last_name) {
        return `${entity.first_name} ${entity.last_name} (${entity.id})`;
      }
      if (entity.id) {
        return `${type} (${entity.id})`;
      }
    }
    return type;
  }

  function createLink(record) {
    let entity = record as { id: string };
    if (entity) {
      return `/admin/${resource}/${entity.id}`;
    }
  }

  function createResourceLabel(): string {
    return resource
      .split("/")
      .map((item) => {
        return item
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      })
      .join(" / ");
  }

  return (
    <Breadcrumb.Item
      name={`admin/${resource}`}
      label={createResourceLabel()}
      to={`/admin/${resource}`}
    >
      <Breadcrumb.Item
        name="edit"
        label={({ record }) => createLabel(record, "Edit")}
        to={({ record }) => createLink(record)}
      ></Breadcrumb.Item>
      <Breadcrumb.Item
        name="create"
        label="Create"
        to={`admin/${resource}/create`}
      ></Breadcrumb.Item>
      <Breadcrumb.Item
        name="show"
        label={({ record }) => createLabel(record, "Show")}
        to={({ record }) => createLink(record)}
      ></Breadcrumb.Item>
    </Breadcrumb.Item>
  );
}
